/**
 * B6 (white-label public POPIA) — public, slug-keyed POPIA officers API.
 *
 * Wraps the anonymous endpoint `GET /v1/popia-officers/by-slug/{slug}`
 * registered by `backend/src/handlers/popia-officers-by-slug.ts`. Drives the
 * public legal pages (`/legal/privacy`, `/legal/paia`) so a visitor on a
 * tenant subdomain (`iprotect.elreign.io`) sees the TENANT's designated
 * Information Officer / Deputy Information Officer in the POPIA + PAIA contact
 * blocks — as POPIA s55 requires — instead of Elreign's platform officer.
 *
 * ## Why this is separate from `lib/api/popia-officers.ts`
 *
 * `popia-officers.ts` wraps the AUTHENTICATED, tenantId-keyed endpoint
 * `GET /v1/tenants/{tenantId}/popia-officers`, used inside the signed-in app
 * shell (`/app/privacy`) where the tenant id is known from the JWT. This
 * module wraps the UNAUTHENTICATED, slug-keyed mirror: the public legal pages
 * render before sign-in and only have the host SLUG, not a tenant id.
 *
 * ## Public shape — no personal PII
 *
 * The backend returns ONLY the designated officer's `displayName` + an
 * ALIAS/role `email` (e.g. `io@<slug>.elreign.io`, or an explicit opt-in
 * contact email) — NEVER the officer's personal `user.email`. The backend
 * tests `popia-officers-by-slug(.cross-tenant).test.ts` assert the personal
 * email is never on the wire.
 *
 * ## Cache + retry policy
 *
 * Mirrors `useTenantBrandingBySlug`: `staleTime: 60_000` matches the
 * `Cache-Control: max-age=60` header, `enabled: !!slug` skips the call on
 * apex / app / local-dev (no tenant subdomain), and a 404 (unknown slug)
 * maps to `null` so the consumer falls back to the Elreign default block.
 * Note: a KNOWN tenant with no designated officer returns 200 with
 * `officers.io === null` (NOT a 404), which the consumer treats the same as
 * the fallback — see `legal/privacy.tsx`.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './client';

export interface PublicPopiaOfficer {
  displayName: string;
  /** Alias / role email (io@<slug>.elreign.io or an explicit opt-in address).
   *  NEVER the officer's personal email. May be null when no alias is
   *  derivable. */
  email: string | null;
}

export interface PublicPopiaOfficers {
  io: PublicPopiaOfficer | null;
  deputyIo: PublicPopiaOfficer | null;
}

export interface PublicPopiaOfficersResponse {
  slug: string;
  officers: PublicPopiaOfficers;
  correlationId: string;
}

/**
 * Fetch a tenant's public POPIA officers by subdomain slug.
 *
 * @param slug Tenant slug resolved from the current host via
 *             `resolveTenantSlugFromHost`. Pass `null` for apex/app/local-dev
 *             to skip the network call entirely (the consumer renders the
 *             Elreign default officer block in that case).
 * @returns    TanStack Query result. `data` is the parsed response when the
 *             slug resolves to a tenant; `null` on 404 (unknown slug). A
 *             known tenant with no designated officer resolves to a 200 whose
 *             `officers.io` / `officers.deputyIo` are null.
 */
export function useTenantPopiaOfficersBySlug(slug: string | null) {
  return useQuery<PublicPopiaOfficersResponse | null>({
    queryKey: ['popia-officers', 'public', slug],
    queryFn: async ({ signal }) => {
      if (!slug) return null;
      try {
        return await api<PublicPopiaOfficersResponse>(
          `/api/v1/popia-officers/by-slug/${encodeURIComponent(slug)}`,
          { signal },
        );
      } catch (err) {
        // 404 = unknown slug → no tenant officer to apply. Fall through to the
        // Elreign default block in the consumer; bubble null, not the error.
        if (
          typeof err === 'object' &&
          err !== null &&
          'status' in err &&
          (err as { status: number }).status === 404
        ) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!slug,
    // Matches the backend `Cache-Control: max-age=60`. Within this window
    // TanStack Query returns the cached value without refetching even on
    // remount, preventing a flash of the default officer block on navigation.
    staleTime: 60_000,
    // Officer designation rarely changes in a session; cache can live for an
    // hour before garbage collection.
    gcTime: 60 * 60_000,
    // Don't retry on 4xx — re-issuing a 404 won't change the answer.
    retry: (failureCount, err) => {
      if (
        typeof err === 'object' &&
        err !== null &&
        'status' in err &&
        typeof (err as { status: number }).status === 'number' &&
        (err as { status: number }).status >= 400 &&
        (err as { status: number }).status < 500
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });
}
